import { useEffect, useState } from "react";

import Loading from "@/components/ui/loading";
import { Button, Modal, Table } from "react-bootstrap";

import api from "@/lib/api";

type ItemModalProps = {
	itemId: number;
	itemIndex: number;
	items: { id: number }[];
	showItemModal: boolean;
	handleCloseItemModal: () => void;
	setItemModal: React.Dispatch<React.SetStateAction<boolean>>;
	setModuleModal: React.Dispatch<React.SetStateAction<boolean>>;
	setSurveyModal: React.Dispatch<React.SetStateAction<boolean>>;
	setItemId: React.Dispatch<React.SetStateAction<number | undefined>>;
	setItemIndex: React.Dispatch<React.SetStateAction<number>>;
};

type ItemModalData = {
	overview: {
		id: number;
		title: string;
		study_title?: string;
	};
	module?: {
		label: string;
	};
	item: {
		title: string;
		description: string;
		item_type?: {
			title: string;
		};
		text?: string;
		item_type_title?: string;
		answer_type_title?: string;
		answer?: string;
	};
};

export async function getItemModalData(itemId: number) {
	try {
		const response = await api.get<ItemModalData>(`/srv/item/${itemId}`);
		return response;
	} catch (error) {
		console.error("Error fetching item data:", error);
		throw new Error("Failed to fetch item data");
	}
}

export default function ItemModal({
	itemId,
	itemIndex,
	items,
	showItemModal,
	handleCloseItemModal,
	setItemModal,
	setModuleModal,
	setSurveyModal,
	setItemId,
	setItemIndex,
}: ItemModalProps) {
	const [itemData, setItemData] = useState<ItemModalData>();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			if (itemId) {
				setLoading(true);
				const response = await getItemModalData(itemId);
				setItemData(response);
				setLoading(false);
			}
		};
		fetchData();
	}, [showItemModal, itemId]);

	const handleNextItem = () => {
		const nextIndex = itemIndex + 1;
		if (nextIndex < items.length) {
			setItemIndex(nextIndex);
			setItemId(items[nextIndex].id);
		}
	};

	const handlePrevItem = () => {
		const prevIndex = itemIndex - 1;
		if (prevIndex >= 0) {
			setItemIndex(prevIndex);
			setItemId(items[prevIndex].id);
		}
	};

	return (
		<Modal
			show={showItemModal}
			onHide={handleCloseItemModal}
			size="lg"
			aria-labelledby="item-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title className="g2-text-grey">
					{itemData?.item.title}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loading ? (
					<Loading />
				) : (
					<>
						<p>
							<span
								className="g2-link-blue"
								onClick={() => {
									setItemModal(false);
									setModuleModal(false);
									setSurveyModal(true);
								}}
							>
								{itemData?.overview?.study_title} {itemData?.overview?.title}
							</span>

							<span className="g2-text-grey"> &gt; </span>
							<span
								className="g2-link-blue"
								onClick={() => {
									setItemModal(false);
									setSurveyModal(false);
									setModuleModal(true);
								}}
							>
								{itemData?.module?.label}
							</span>
							<span className="g2-text-grey"> &gt; </span>
							<span className="g2-text-grey">{itemData?.item?.title}</span>
						</p>
						<Table bordered striped responsive width="100%">
							<tbody>
								<tr>
									<td className="fw-bold" style={{ width: "25%" }}>
										Description:
									</td>
									<td>{itemData?.item.description}</td>
								</tr>
								<tr>
									<td className="fw-bold">Item type:</td>
									<td>{itemData?.item.item_type_title}</td>
								</tr>
								<tr>
									<td className="fw-bold">Question text:</td>
									<td>{itemData?.item.text}</td>
								</tr>
								<tr>
									<td className="fw-bold">Answer type:</td>
									<td>{itemData?.item.answer_type_title}</td>
								</tr>
								<tr>
									<td className="fw-bold">Answer choices:</td>
									<td>{itemData?.item.answer}</td>
								</tr>
							</tbody>
						</Table>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{itemIndex !== 0 && (
					<Button className="g2-btn-blue" onClick={handlePrevItem}>
						Previous
					</Button>
				)}
				{itemIndex !== items.length - 1 && (
					<Button className="g2-btn-blue" onClick={handleNextItem}>
						Next
					</Button>
				)}
				<Button variant="secondary" onClick={handleCloseItemModal}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
